import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, Menu, X, AlertTriangle, MapPin, Mail } from "lucide-react";
import {
  PHONE_DISPLAY,
  PHONE_TEL,
  EMAIL,
  SERVICE_AREA,
  BRAND,
  LOGO_URL,
} from "../lib/constants";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const EmergencyBanner = () => (
  <div
    data-testid="emergency-banner"
    className="bg-brand text-black border-b-2 border-black/20 overflow-hidden"
  >
    <div className="relative flex whitespace-nowrap">
      <div className="animate-marquee flex shrink-0 items-center gap-12 py-2.5 px-6 text-xs sm:text-sm font-bold uppercase tracking-[0.18em]">
        {Array.from({ length: 8 }).map((_, i) => (
          <span key={i} className="flex items-center gap-3">
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} />
            24/7 Emergency Recovery
            <span className="text-black/60">•</span>
            Call {PHONE_DISPLAY}
            <span className="text-black/60">•</span>
            Fast UK Response
            <span className="text-black/60">•</span>
          </span>
        ))}
      </div>
      <div
        aria-hidden
        className="animate-marquee flex shrink-0 items-center gap-12 py-2.5 px-6 text-xs sm:text-sm font-bold uppercase tracking-[0.18em]"
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <span key={i} className="flex items-center gap-3">
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} />
            24/7 Emergency Recovery
            <span className="text-black/60">•</span>
            Call {PHONE_DISPLAY}
            <span className="text-black/60">•</span>
            Fast UK Response
            <span className="text-black/60">•</span>
          </span>
        ))}
      </div>
    </div>
  </div>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/95 backdrop-blur-md border-b border-white/10"
          : "bg-black/80 backdrop-blur-sm border-b border-white/5"
      }`}
    >
      <EmergencyBanner />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 group"
          >
            <img
              src={LOGO_URL}
              alt={`${BRAND} logo`}
              className="h-11 w-11 object-contain"
              data-testid="brand-logo"
            />
            <div className="leading-none">
              <div className="font-display font-extrabold text-lg sm:text-xl tracking-tight">
                Express<span className="text-brand">Breakdown</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mt-0.5">
                UK Recovery · 24/7
              </div>
            </div>
          </Link>

          <nav
            data-testid="desktop-nav"
            className="hidden lg:flex items-center gap-1"
          >
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                    isActive
                      ? "text-brand"
                      : "text-white/80 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={PHONE_TEL}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-5 py-3 btn-brutalist"
            >
              <Phone className="h-4 w-4" strokeWidth={2.5} />
              {PHONE_DISPLAY}
            </a>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((s) => !s)}
              className="lg:hidden p-2 text-white"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-nav"
            className="lg:hidden pb-6 pt-2 border-t border-white/10"
          >
            <div className="flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base font-semibold uppercase tracking-wider border-l-2 ${
                      isActive
                        ? "border-brand text-brand"
                        : "border-transparent text-white/80"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <a
                href={PHONE_TEL}
                data-testid="mobile-call-btn"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-5 py-4"
              >
                <Phone className="h-4 w-4" strokeWidth={2.5} />
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="border-t border-white/10 bg-black mt-0"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt={`${BRAND}`}
              className="h-12 w-12 object-contain"
            />
            <div className="font-display font-extrabold text-xl">
              Express<span className="text-brand">Breakdown</span>
            </div>
          </div>
          <p className="text-sm text-white/60 mt-6 max-w-sm leading-relaxed">
            {BRAND} provides vehicle breakdown recovery and roadside support.
            Response times depend on location, traffic, vehicle type and
            availability. Always move to a safe location before calling where
            possible.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">
            Navigate
          </div>
          <ul className="mt-5 space-y-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`footer-link-${n.label.toLowerCase()}`}
                  className="text-sm text-white/70 hover:text-brand transition-colors"
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">
            Contact
          </div>
          <ul className="mt-5 space-y-4 text-sm">
            <li>
              <a
                href={PHONE_TEL}
                data-testid="footer-phone-link"
                className="flex items-center gap-3 text-white hover:text-brand transition-colors"
              >
                <Phone className="h-4 w-4 text-brand" strokeWidth={2.5} />
                <span className="font-bold text-lg">{PHONE_DISPLAY}</span>
              </a>
            </li>
            <li className="flex items-center gap-3 text-white/70">
              <Mail className="h-4 w-4 text-brand" strokeWidth={2.5} />
              {EMAIL}
            </li>
            <li className="flex items-center gap-3 text-white/70">
              <MapPin className="h-4 w-4 text-brand" strokeWidth={2.5} />
              {SERVICE_AREA}
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="text-xs text-white/50 uppercase tracking-wider">
          © {new Date().getFullYear()} {BRAND}. All rights reserved.
        </div>
        <div className="text-xs text-white/50 uppercase tracking-wider">
          Breakdown · Recovery · Roadside
        </div>
      </div>
    </div>
  </footer>
);

const FloatingCallButton = () => (
  <a
    href={PHONE_TEL}
    data-testid="floating-call-btn"
    className="lg:hidden fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-5 py-4 shadow-[0_10px_30px_-10px_rgba(255,212,0,0.6)]"
  >
    <span className="relative flex h-2.5 w-2.5 pulse-dot rounded-full bg-black"></span>
    <Phone className="h-4 w-4" strokeWidth={2.5} />
    Call Now
  </a>
);

export default function Layout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <FloatingCallButton />
    </div>
  );
}
